import { ParticipationWorkshop } from '../entities/ParticipationWorkshop.js';
import { map, of } from 'rxjs';
import { readEntitiesByIDs, watchEntitiesByIDs, readAllEntities, watchAllEntities } from '../server/entity-reads.js';
import { type BaseQLSelectionSet } from '@adornis/baseql/utils/queryGeneration.js';
import { registerQuery, registerSubscription } from '@adornis/baseql/metadata/register.js';
import { ID, Int } from '@adornis/baseql/baseqlTypes.js';

export const getParticipationWorkshopsByIDs = registerQuery({
  operationName: 'getParticipationWorkshopsByIDs',
  type: () => [ParticipationWorkshop],
  params: [{ name: 'ids', type: () => [ID] }],
  resolve:
    (ids: string[]) =>
    <SelSet extends BaseQLSelectionSet<ParticipationWorkshop>>(gqlFields: SelSet) =>
      readEntitiesByIDs(ParticipationWorkshop, ids, gqlFields),
});

export const subscribeParticipationWorkshopsByIDs = registerSubscription({
  operationName: 'subscribeParticipationWorkshopsByIDs',
  type: () => [ParticipationWorkshop],
  params: [{ name: 'ids', type: () => [ID] }],
  resolve:
    (ids: string[]) =>
    <SelSet extends BaseQLSelectionSet<ParticipationWorkshop>>(gqlFields: SelSet) =>
      watchEntitiesByIDs(ParticipationWorkshop, ids, gqlFields),
});

export const getAllParticipationWorkshops = registerQuery({
  operationName: 'getAllParticipationWorkshops',
  type: () => [ParticipationWorkshop],
  params: [
    { name: 'skip', type: () => Int },
    { name: 'limit', type: () => Int },
    { name: 'excludeIDs', type: () => [ID] },
  ],
  resolve:
    (skip?: number, limit?: number, excludeIDs?: string[]) =>
    <SelSet extends BaseQLSelectionSet<ParticipationWorkshop>>(gqlFields: SelSet) =>
      readAllEntities(ParticipationWorkshop, gqlFields, { skip, limit, excludeIDs }),
});

export const subscribeAllParticipationWorkshops = registerSubscription({
  operationName: 'subscribeAllParticipationWorkshops',
  type: () => [ParticipationWorkshop],
  params: [
    { name: 'skip', type: () => Int },
    { name: 'limit', type: () => Int },
    { name: 'excludeIDs', type: () => [ID] },
  ],
  resolve:
    (skip?: number, limit?: number, excludeIDs?: string[]) =>
    <SelSet extends BaseQLSelectionSet<ParticipationWorkshop>>(gqlFields: SelSet) =>
      watchAllEntities(ParticipationWorkshop, gqlFields, { skip, limit, excludeIDs }),
});

export const getParticipationWorkshopByID =
  (id: string | null | undefined) =>
  async <SelSet extends BaseQLSelectionSet<ParticipationWorkshop>>(gqlFields: SelSet) => {
    if (!id) return null;
    return (await getParticipationWorkshopsByIDs([id])(gqlFields))[0] ?? null;
  };

export const subscribeParticipationWorkshopByID =
  (id: string | null | undefined) =>
  <SelSet extends BaseQLSelectionSet<ParticipationWorkshop>>(gqlFields: SelSet) =>
    id ? subscribeParticipationWorkshopsByIDs([id])(gqlFields).pipe(map(entities => entities[0] ?? null)) : of(null);
